import { authService, type AuthState } from '../services/auth';
import { offlineService } from '../services/offline';
import type { User, UserTeam, Game, Player, PitchCount, Team, Season } from '../services/api';

export type Tab = 'stats' | 'count' | 'admin';
export type CountView = 'select-game' | 'counting';

// URL parameter names
const GAME_PARAM = 'game';
const TAB_PARAM = 'tab';
const VIEW_PARAM = 'view';
const PUBLIC_PARAM = 'public';

export interface AppStateData {
  // Auth
  auth: AuthState;

  // Navigation
  activeTab: Tab;
  countView: CountView;

  // Current game tracking
  currentGame: Game | null;
  currentPlayers: Player[];
  currentPitches: Map<string, PitchCount>; // player_id -> PitchCount
  selectedPlayerId: string | null;

  // Cached data
  teams: Team[];
  seasons: Season[];
  games: Game[];

  // Sync state
  isOnline: boolean;
  pendingCount: number;

  // Public (signed-out) browsing mode. When true, the app renders a
  // read-only Stats view + game viewer without going through Firebase
  // auth. Set from the URL param `?public=1` on init and toggled by the
  // sign-in card / header buttons.
  publicMode: boolean;

  // When set in public mode, App renders <public-game-view> instead of
  // the Stats list. Cleared by tapping "Back" or switching to the
  // sign-in flow. Authenticated game viewing still uses currentGame.
  publicGameId: string | null;
}

type StateListener = (state: AppStateData) => void;

class AppStateStore {
  private state: AppStateData = {
    auth: { loading: true, user: null, firebaseUser: null },
    activeTab: 'count',
    countView: 'select-game',
    currentGame: null,
    currentPlayers: [],
    currentPitches: new Map(),
    selectedPlayerId: null,
    teams: [],
    seasons: [],
    games: [],
    isOnline: navigator.onLine,
    pendingCount: 0,
    publicMode: AppStateStore.readPublicModeFromUrl(),
    publicGameId: AppStateStore.readPublicGameIdFromUrl(),
  };

  private listeners: Set<StateListener> = new Set();

  constructor() {
    // Subscribe to auth changes
    authService.subscribe((authState) => {
      this.setState({ auth: authState });
    });

    // Subscribe to offline service
    offlineService.subscribe((syncState) => {
      this.setState({
        isOnline: syncState.isOnline,
        pendingCount: syncState.pendingCount,
      });
    });
  }

  getState(): AppStateData {
    return this.state;
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  setState(partial: Partial<AppStateData>): void {
    this.state = { ...this.state, ...partial };
    this.listeners.forEach((listener) => listener(this.state));
  }

  // Actions
  setActiveTab(tab: Tab, options: { updateUrl?: boolean; replaceState?: boolean } = {}): void {
    const { updateUrl = true, replaceState = false } = options;
    this.setState({ activeTab: tab });

    if (updateUrl) {
      this.updateUrlWithTab(tab, replaceState);
    }
  }

  setCountView(view: CountView): void {
    this.setState({ countView: view });
  }

  setCurrentGame(game: Game | null, options: { updateUrl?: boolean; replaceState?: boolean } = {}): void {
    const { updateUrl = true, replaceState = false } = options;

    this.setState({
      currentGame: game,
      countView: game ? 'counting' : 'select-game',
      currentPlayers: [],
      currentPitches: new Map(),
      selectedPlayerId: null,
    });

    // Update URL with game ID
    if (updateUrl) {
      this.updateUrlWithGame(game?.id || null, replaceState);
    }
  }

  /**
   * Update URL search params with game ID
   */
  private updateUrlWithGame(gameId: string | null, replaceState = false): void {
    const url = new URL(window.location.href);

    if (gameId) {
      url.searchParams.set(GAME_PARAM, gameId);
    } else {
      url.searchParams.delete(GAME_PARAM);
    }

    const method = replaceState ? 'replaceState' : 'pushState';
    window.history[method]({ gameId }, '', url.toString());
  }

  /**
   * Get game ID from URL search params
   */
  getGameIdFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(GAME_PARAM);
  }

  /**
   * Update URL search params with tab
   */
  private updateUrlWithTab(tab: Tab, replaceState = false): void {
    const url = new URL(window.location.href);

    // Only set tab param for non-default tabs
    if (tab === 'count') {
      url.searchParams.delete(TAB_PARAM);
    } else {
      url.searchParams.set(TAB_PARAM, tab);
    }

    // Clear game param when switching tabs
    url.searchParams.delete(GAME_PARAM);

    // Clear view param when not on admin tab
    if (tab !== 'admin') {
      url.searchParams.delete(VIEW_PARAM);
    }

    const method = replaceState ? 'replaceState' : 'pushState';
    window.history[method]({ tab }, '', url.toString());
  }

  /**
   * Get tab from URL search params
   */
  getTabFromUrl(): Tab | null {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get(TAB_PARAM);
    if (tab === 'stats' || tab === 'count' || tab === 'admin') {
      return tab;
    }
    return null;
  }

  /**
   * Update URL search params with admin view
   */
  updateUrlWithView(view: string | null, replaceState = false): void {
    const url = new URL(window.location.href);

    if (view) {
      url.searchParams.set(VIEW_PARAM, view);
    } else {
      url.searchParams.delete(VIEW_PARAM);
    }

    const method = replaceState ? 'replaceState' : 'pushState';
    window.history[method]({ view }, '', url.toString());
  }

  /**
   * Get admin view from URL search params
   */
  getViewFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(VIEW_PARAM);
  }

  /**
   * Clear game from URL (used when navigating away)
   */
  clearGameFromUrl(): void {
    this.updateUrlWithGame(null, true);
  }

  /**
   * Read the public-mode flag from the URL during construction. Static so
   * the field initializer can call it before `this` is fully ready.
   * Accepts `public=1` (canonical) or any truthy non-empty value.
   */
  private static readPublicModeFromUrl(): boolean {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const val = params.get(PUBLIC_PARAM);
    return val !== null && val !== '0' && val !== '';
  }

  /**
   * Read a `?public_game=<id>` deep link, used so a guest can refresh on a
   * specific game-detail view and stay there.
   */
  private static readPublicGameIdFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('public_game');
  }

  /**
   * Enter or leave public (guest browsing) mode. Updates the URL so the
   * state survives a refresh and is shareable. When leaving public mode
   * we also clear any public-game deep link.
   */
  setPublicMode(enabled: boolean, options: { updateUrl?: boolean } = {}): void {
    const { updateUrl = true } = options;
    const partial: Partial<AppStateData> = { publicMode: enabled };
    if (!enabled) {
      partial.publicGameId = null;
    } else {
      // Force the public-mode landing surface to be Stats.
      partial.activeTab = 'stats';
    }
    this.setState(partial);

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (enabled) {
        url.searchParams.set(PUBLIC_PARAM, '1');
      } else {
        url.searchParams.delete(PUBLIC_PARAM);
        url.searchParams.delete('public_game');
      }
      window.history.replaceState({ publicMode: enabled }, '', url.toString());
    }
  }

  /**
   * Open a game in the public read-only viewer. Pushes `?public_game=<id>`
   * onto history so the guest can bookmark/share the URL and the Back
   * button returns to the team directory.
   */
  setPublicGameId(gameId: string | null, options: { updateUrl?: boolean; replaceState?: boolean } = {}): void {
    const { updateUrl = true, replaceState = false } = options;
    this.setState({ publicGameId: gameId });

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (gameId) {
        url.searchParams.set('public_game', gameId);
      } else {
        url.searchParams.delete('public_game');
      }
      const method = replaceState ? 'replaceState' : 'pushState';
      window.history[method]({ publicGameId: gameId }, '', url.toString());
    }
  }

  /**
   * Re-sync public-mode flags from the current URL. Called by App.ts on
   * popstate so browser Back/Forward updates the rendered view.
   */
  syncPublicStateFromUrl(): void {
    this.setState({
      publicMode: AppStateStore.readPublicModeFromUrl(),
      publicGameId: AppStateStore.readPublicGameIdFromUrl(),
    });
  }

  setCurrentPlayers(players: Player[]): void {
    this.setState({ currentPlayers: players });
  }

  setCurrentPitches(pitches: PitchCount[]): void {
    const map = new Map<string, PitchCount>();
    pitches.forEach((p) => map.set(p.player_id, p));
    this.setState({ currentPitches: map });
  }

  updatePitchCount(playerId: string, count: number, pitchId?: string): void {
    const newPitches = new Map(this.state.currentPitches);
    const existing = newPitches.get(playerId);

    if (existing) {
      newPitches.set(playerId, { ...existing, count, id: pitchId || existing.id });
    } else {
      // Create new entry with temporary or real ID for optimistic updates
      newPitches.set(playerId, {
        id: pitchId || `temp-${playerId}`,
        game_id: this.state.currentGame?.id || '',
        player_id: playerId,
        count,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }

    this.setState({ currentPitches: newPitches });
  }

  selectPlayer(playerId: string | null): void {
    this.setState({ selectedPlayerId: playerId });
  }

  setTeams(teams: Team[]): void {
    this.setState({ teams });
  }

  setSeasons(seasons: Season[]): void {
    this.setState({ seasons });
  }

  setGames(games: Game[]): void {
    this.setState({ games });
  }

  // Getters
  get user(): User | null {
    return this.state.auth.user;
  }

  get isAdmin(): boolean {
    return this.state.auth.user?.is_admin ?? false;
  }

  get isAuthenticated(): boolean {
    return this.state.auth.user !== null;
  }

  get isLoading(): boolean {
    return this.state.auth.loading;
  }

  get isCoach(): boolean {
    const user = this.state.auth.user;
    return !!(user && user.teams && user.teams.length > 0);
  }

  get userTeams(): UserTeam[] {
    return this.state.auth.user?.teams ?? [];
  }

  get userTeamIds(): string[] {
    return this.state.auth.user?.team_ids ?? [];
  }

  get isPublicMode(): boolean {
    return this.state.publicMode;
  }

  get hasNoTeams(): boolean {
    const user = this.state.auth.user;
    if (!user || user.is_admin) return false;
    // Only show empty state if team_ids was explicitly provided (empty array)
    // If team_ids is undefined, the session hasn't been enriched with team data yet
    if (!Array.isArray(user.team_ids)) return false;
    return user.team_ids.length === 0;
  }

  getPlayerPitchCount(playerId: string): number {
    return this.state.currentPitches.get(playerId)?.count ?? 0;
  }
}

export const appState = new AppStateStore();
